import { Link } from '@tanstack/react-router';
import {
    AlertCircle,
    Loader2,
    Megaphone,
    MessageSquareText,
} from 'lucide-react';
import React from 'react';

import {
    ConversationMessageNotes,
    type ConversationMessageNotesSummary,
} from '@apps/web/components/shared/messages/conversation_message_notes';
import { splitTextBySearchQuery } from '@apps/web/components/shared/messages/conversation_search_utils';
import { MessageRatingButtons } from '@apps/web/components/shared/messages/message_rating_buttons';
import { MessageRatingTextarea } from '@apps/web/components/shared/messages/message_rating_textarea';
import { SmsRunRatingControl } from '@apps/web/components/shared/messages/sms_run_rating_control';
import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@apps/web/components/ui/tooltip';
import {
    getConversationAvatarToneClass,
    UserAvatar,
} from '@apps/web/components/ui/user_avatar';
import { useMessageRating } from '@apps/web/hooks/use_message_rating';
import { getApiClient } from '@apps/web/lib/api_client';
import {
    getBlockedPhoneErrorMessage,
    getBlockedPhoneShortMessage,
} from '@apps/web/lib/blocked_phone_utils';
import { cn } from '@apps/web/lib/utils';
import {
    useAdminMessageRatings,
    useUser,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import {
    type Message,
    type MessageRatingLabel,
} from '@libraries/typescript/api_client';

type SmsRunRating = {
    readonly run_id: string;
    readonly rating_label: MessageRatingLabel | null;
};

type MessageAttachment = {
    readonly id: string;
    readonly content_type: string | null;
    readonly storage_status: 'pending' | 'ready' | 'rejected' | 'unavailable';
};

function MessageAttachmentPreview({
    attachment,
    isInbound,
    organizationId,
}: {
    readonly attachment: MessageAttachment;
    readonly isInbound: boolean;
    readonly organizationId?: string | null;
}) {
    const [contentUrl, setContentUrl] = React.useState<string | null>(null);
    const [failedToLoad, setFailedToLoad] = React.useState(false);

    React.useEffect(() => {
        setContentUrl(null);
        setFailedToLoad(false);
        if (attachment.storage_status !== 'ready' || !organizationId) {
            return;
        }

        const abortController = new AbortController();
        let objectUrl: string | null = null;
        void (async () => {
            try {
                const response = await getApiClient().raw({
                    method: 'GET',
                    url: '/organizations/{organization_id}/conversations/attachments/{attachment_id}/content',
                    pathParams: {
                        organization_id: organizationId,
                        attachment_id: attachment.id,
                    },
                    reason: 'message-attachment-thumbnail',
                    signal: abortController.signal,
                });
                const blob = await response.blob();
                objectUrl = URL.createObjectURL(blob);
                if (abortController.signal.aborted) {
                    URL.revokeObjectURL(objectUrl);
                    objectUrl = null;
                    return;
                }
                setContentUrl(objectUrl);
            } catch (error) {
                if (!abortController.signal.aborted) {
                    console.error('Failed to load message attachment', error);
                    setFailedToLoad(true);
                }
            }
        })();

        return () => {
            abortController.abort();
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [attachment.id, attachment.storage_status, organizationId]);

    if (
        attachment.storage_status !== 'ready' ||
        !organizationId ||
        failedToLoad
    ) {
        return (
            <div
                className={cn(
                    'flex min-h-24 items-center justify-center rounded-lg border border-dashed px-4 text-xs',
                    isInbound
                        ? 'text-muted-foreground'
                        : 'border-white/40 text-white/80',
                )}
            >
                {attachment.storage_status === 'pending'
                    ? 'Processing image…'
                    : 'Image unavailable'}
            </div>
        );
    }
    if (!contentUrl) {
        return (
            <div className="text-muted-foreground flex min-h-24 items-center justify-center rounded-lg border border-dashed px-4 text-xs">
                Loading image…
            </div>
        );
    }

    return (
        <img
            alt="Message attachment"
            className="max-h-80 w-full rounded-lg object-contain"
            loading="lazy"
            onError={() => setFailedToLoad(true)}
            src={contentUrl}
        />
    );
}

type MessageItemProps = {
    readonly message: Message & {
        readonly callout_id?: string | null;
        readonly has_internal_notes?: boolean;
        readonly outreach_type?:
            | 'callout'
            | 'clock_in'
            | 'clock_out'
            | 'recurring_shift'
            | 'shift_confirmation'
            | 'survey'
            | null;
    };
    readonly timezone?: string;
    readonly isHighlighted?: boolean;
    readonly highlightLabel?: string;
    readonly showRating?: boolean;
    readonly showDate?: boolean;
    readonly smsEngineRunId?: string;
    // Coordinator SMS training review: thumbs on Phoebe's reply when it can be
    // resolved (else the inbound message), mirroring the admin `showRating`
    // surface. Additive and only wired for training-mode orgs (coordinator
    // panes pass `showRating={false}`, so it never collides with the admin
    // thumbs).
    readonly organizationId?: string | null;
    readonly smsTrainingReview?: boolean;
    readonly smsRunRatings?: ReadonlyArray<SmsRunRating>;
    readonly onSmsRunRated?: () => void;
    readonly searchQuery?: string | null;
    readonly isSearchActiveMatch?: boolean;
    // Renders the visible "Delivered" footer. The conversation timeline sets
    // it on the newest outbound message only (iMessage-style); every other
    // delivered message still says so beside its hover timestamp.
    readonly showDeliveryReceipt?: boolean;
    readonly contactName?: string | null;
    readonly contactColorKey?: string;
    readonly showMessageNotes?: boolean;
    readonly openNotesInitially?: boolean;
    readonly onConversationMutated?: () => Promise<unknown> | void;
};

function renderMessageContent(
    content: string,
    searchQuery: string | null | undefined,
) {
    if (!searchQuery) {
        return content;
    }
    return splitTextBySearchQuery(content, searchQuery).map((segment, index) =>
        segment.isMatch ? (
            <mark
                key={index}
                className="rounded bg-yellow-300 px-0.5 text-black"
            >
                {segment.text}
            </mark>
        ) : (
            <React.Fragment key={index}>{segment.text}</React.Fragment>
        ),
    );
}

export function MessageItem({
    message,
    timezone,
    isHighlighted = false,
    highlightLabel,
    showRating = true,
    showDate = false,
    smsEngineRunId,
    organizationId,
    smsTrainingReview = false,
    smsRunRatings = [],
    onSmsRunRated,
    searchQuery = null,
    isSearchActiveMatch = false,
    showDeliveryReceipt = false,
    contactName = null,
    contactColorKey,
    showMessageNotes = false,
    openNotesInitially = false,
    onConversationMutated,
}: MessageItemProps) {
    const [notesOpen, setNotesOpen] = React.useState(() =>
        Boolean(showMessageNotes && message.has_internal_notes),
    );
    const [threadExists, setThreadExists] = React.useState(
        message.has_internal_notes ?? false,
    );
    const [notesSummary, setNotesSummary] =
        React.useState<ConversationMessageNotesSummary | null>(null);
    const notesTriggerRef = React.useRef<HTMLButtonElement | null>(null);
    const notesThreadRef = React.useRef<HTMLDivElement | null>(null);
    const messageActionsRef = React.useRef<HTMLDivElement | null>(null);
    const messageActionsCloseTimerRef = React.useRef<ReturnType<
        typeof setTimeout
    > | null>(null);
    const [messageActionsOpen, setMessageActionsOpen] = React.useState(false);

    React.useEffect(() => {
        if (openNotesInitially && threadExists) {
            setNotesOpen(true);
        }
    }, [openNotesInitially, threadExists]);

    React.useEffect(
        () => () => {
            if (messageActionsCloseTimerRef.current !== null) {
                clearTimeout(messageActionsCloseTimerRef.current);
            }
        },
        [],
    );

    function keepMessageActionsOpen() {
        if (messageActionsCloseTimerRef.current !== null) {
            clearTimeout(messageActionsCloseTimerRef.current);
            messageActionsCloseTimerRef.current = null;
        }
        setMessageActionsOpen(true);
    }

    function closeMessageActionsAfterPointerGap() {
        if (messageActionsCloseTimerRef.current !== null) {
            clearTimeout(messageActionsCloseTimerRef.current);
        }
        messageActionsCloseTimerRef.current = setTimeout(() => {
            setMessageActionsOpen(false);
            messageActionsCloseTimerRef.current = null;
        }, 200);
    }

    React.useEffect(() => {
        if (message.has_internal_notes) {
            setThreadExists(true);
            setNotesOpen(true);
        }
    }, [message.has_internal_notes]);

    React.useEffect(() => {
        if (!notesOpen) {
            return;
        }

        function closeNotesOnOutsidePointerDown(event: PointerEvent) {
            if (!(event.target instanceof Node)) {
                return;
            }
            if (
                notesTriggerRef.current?.contains(event.target) ||
                notesThreadRef.current?.contains(event.target) ||
                messageActionsRef.current?.contains(event.target)
            ) {
                return;
            }
            setNotesOpen(false);
        }

        document.addEventListener(
            'pointerdown',
            closeNotesOnOutsidePointerDown,
        );
        return () => {
            document.removeEventListener(
                'pointerdown',
                closeNotesOnOutsidePointerDown,
            );
        };
    }, [notesOpen]);

    const isInbound = message.role === 'user';
    const attachments =
        (
            message as Message & {
                readonly attachments?: ReadonlyArray<MessageAttachment>;
            }
        ).attachments ?? [];
    const [showOriginalText, setShowOriginalText] = React.useState(false);
    const sentStatus = message.sent_status ?? 'sent';
    const isPending = !isInbound && sentStatus === 'pending';
    const isFailed = !isInbound && sentStatus === 'failed';
    // The carrier's receipt that the text reached the phone. It is what lets
    // a coordinator tell "the phone got it, so the device is blocking or
    // filtering Phoebe's number" apart from a send failure when a caregiver
    // reports not receiving texts.
    const isDelivered =
        !isInbound &&
        sentStatus === 'sent' &&
        message.provider_status === 'delivered';
    const hasErrorCode =
        isFailed &&
        message.error_code !== null &&
        message.error_code !== undefined;
    const errorCodeString = hasErrorCode ? String(message.error_code) : null;
    const shortFailureLabel = errorCodeString
        ? getBlockedPhoneShortMessage(errorCodeString, 'twilio')
        : null;
    const fullFailureReason = errorCodeString
        ? getBlockedPhoneErrorMessage(errorCodeString, 'twilio')
        : null;
    const outboundSenderName = !isInbound
        ? (message.created_by_user_name ?? 'Phoebe')
        : null;
    const inboundContactName = isInbound ? contactName?.trim() || '?' : null;
    const avatarName = outboundSenderName ?? inboundContactName ?? '?';
    const avatarIsPhoebe = outboundSenderName === 'Phoebe';
    const { data: user } = useUser({ reason: 'message-item' });
    const formattedTime = new Date(message.created_at).toLocaleString('en-US', {
        ...(showDate && { month: 'short', day: 'numeric' }),
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        ...(timezone && { timeZone: timezone }),
    });

    const { data: ratingResponse, mutate } = useAdminMessageRatings(
        message.conversation_id,
        (showRating && user?.admin) ?? false,
    );

    const rating =
        ratingResponse?.ratings.find(
            (rating) => rating.message_id === message.id,
        ) ?? null;

    const {
        selectedLabel,
        isSaving,
        draftComment,
        hasUnsavedComment,
        isOperationPending,
        handleLabelClick,
        handleCommentChange,
        handleSaveComment,
    } = useMessageRating({
        rating,
        mutateAllRatings: mutate,
        messageId: message.id,
        isAdmin: showRating && user?.admin,
    });
    // Badge messages that flowed through an external phone system or a
    // simulator-owned inbound source rather than Phoebe.
    const forwardedMessageBadgeLabel =
        message.source_display_label ??
        (message.source === 'ringcentral'
            ? 'RingCentral'
            : message.source === 'dialpad'
              ? 'Dialpad'
              : message.source === 'external'
                ? 'Phone system'
                : null);

    const messageAvatar = avatarIsPhoebe ? (
        <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-[#5533EE] text-white">
            <img
                src="/phoebe.svg"
                alt=""
                aria-hidden="true"
                draggable={false}
                className="size-4 object-contain invert"
            />
        </div>
    ) : (
        <UserAvatar
            className="size-8 shrink-0"
            colorKey={contactColorKey ?? avatarName}
            displayName={avatarName}
            toneClassName={getConversationAvatarToneClass(
                isInbound ? (contactColorKey ?? avatarName) : avatarName,
            )}
        />
    );

    const showBubbleMeta =
        Boolean(isHighlighted && highlightLabel) ||
        Boolean(forwardedMessageBadgeLabel) ||
        Boolean(user?.admin && smsEngineRunId);
    const canShowNotesAction = Boolean(showMessageNotes && organizationId);
    const hasMessageActions = canShowNotesAction || Boolean(message.callout_id);

    return (
        <div>
            <div
                className={cn(
                    'group/message flex items-end gap-1.5',
                    isInbound ? 'justify-start' : 'justify-end',
                    isHighlighted && 'relative',
                )}
                data-testid="message-row"
                onPointerEnter={keepMessageActionsOpen}
                onPointerLeave={closeMessageActionsAfterPointerGap}
            >
                {isInbound ? messageAvatar : null}
                <div
                    className="max-w-[min(80%,680px)] min-w-0"
                    data-testid="message-content"
                >
                    <div className="relative">
                        <div
                            className={cn(
                                'rounded-2xl p-3',
                                isInbound
                                    ? 'bg-muted'
                                    : 'bg-[#007AFF] text-white',
                                isPending && 'bg-[#007AFF]/70',
                                isFailed &&
                                    'border border-red-400/30 bg-red-500/10 text-red-700 dark:text-red-400',
                                (isHighlighted || isSearchActiveMatch) &&
                                    'ring-2 ring-blue-500',
                            )}
                        >
                            {showBubbleMeta ? (
                                <div className="mb-1 flex items-center gap-2">
                                    {isHighlighted && highlightLabel && (
                                        <span className="rounded bg-blue-500/20 px-1.5 py-0.5 text-xs font-medium">
                                            {highlightLabel}
                                        </span>
                                    )}
                                    {forwardedMessageBadgeLabel && (
                                        <Badge
                                            variant="outline"
                                            className="border-sky-200 bg-sky-50 px-1.5 py-0 text-[11px] text-sky-700 hover:bg-sky-50 dark:border-sky-900 dark:bg-sky-950/50 dark:text-sky-300 dark:hover:bg-sky-950/50"
                                        >
                                            {forwardedMessageBadgeLabel}
                                        </Badge>
                                    )}
                                    {user?.admin && smsEngineRunId && (
                                        <Link
                                            to="/admin/sms-engine-runs/$runId"
                                            params={{ runId: smsEngineRunId }}
                                            className={cn(
                                                'text-[11px] font-medium underline underline-offset-2',
                                                isInbound
                                                    ? 'text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300'
                                                    : 'text-white/80 hover:text-white',
                                            )}
                                        >
                                            SMS run
                                        </Link>
                                    )}
                                </div>
                            ) : null}
                            {attachments.length ? (
                                <div className="mb-2 grid max-w-sm gap-2">
                                    {attachments.map((attachment) => (
                                        <MessageAttachmentPreview
                                            attachment={attachment}
                                            isInbound={isInbound}
                                            key={attachment.id}
                                            organizationId={organizationId}
                                        />
                                    ))}
                                </div>
                            ) : null}
                            {message.content ? (
                                <p
                                    // Keyed by displayed language so the swap
                                    // remounts the text and replays the fade.
                                    key={
                                        showOriginalText
                                            ? 'original'
                                            : 'english'
                                    }
                                    className={cn(
                                        'text-sm [overflow-wrap:anywhere] whitespace-pre-wrap',
                                        message.translation &&
                                            'animate-in fade-in duration-150',
                                    )}
                                >
                                    {renderMessageContent(
                                        // `translated_text` is always the English
                                        // side (translation of an inbound, original
                                        // draft of a translated outbound); show it
                                        // by default and let the toggle reveal the
                                        // message as actually sent or received.
                                        message.translation && !showOriginalText
                                            ? message.translation
                                                  .translated_text
                                            : message.content,
                                        searchQuery,
                                    )}
                                </p>
                            ) : null}
                            {message.translation && (
                                <button
                                    type="button"
                                    // Pending-message cards wrap this bubble in
                                    // a navigation <Link>; keep the toggle from
                                    // bubbling into it and navigating away.
                                    onClick={(event) => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        setShowOriginalText(!showOriginalText);
                                    }}
                                    className={cn(
                                        'mt-1 text-[10px] underline underline-offset-2',
                                        isInbound
                                            ? 'text-muted-foreground hover:text-foreground/80'
                                            : 'text-white/80 hover:text-white',
                                    )}
                                >
                                    {showOriginalText
                                        ? 'View in English'
                                        : 'View original message'}
                                </button>
                            )}
                        </div>
                        {hasMessageActions ? (
                            <div
                                className={cn(
                                    "bg-background absolute bottom-full z-10 mb-1 flex h-10 items-center rounded-xl border p-1 shadow-sm transition-opacity duration-150 before:absolute before:-inset-x-3 before:top-full before:h-4 before:content-['']",
                                    isInbound
                                        ? '-right-4 flex-row'
                                        : '-left-4 flex-row-reverse',
                                    messageActionsOpen
                                        ? 'pointer-events-auto opacity-100'
                                        : 'pointer-events-none opacity-0',
                                    'group-focus-within/message:pointer-events-auto group-focus-within/message:opacity-100',
                                )}
                                data-testid="message-hover-actions"
                                onClick={(event) => event.stopPropagation()}
                                ref={messageActionsRef}
                            >
                                {message.callout_id ? (
                                    <Tooltip>
                                        <TooltipTrigger asChild={true}>
                                            <Link
                                                aria-label="Open related outreach"
                                                className="text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:ring-ring/50 flex size-8 items-center justify-center rounded-md outline-none focus-visible:ring-[3px]"
                                                params={{
                                                    'callout-id':
                                                        message.callout_id,
                                                }}
                                                to="/outreaches/$callout-id"
                                            >
                                                <Megaphone className="size-4" />
                                            </Link>
                                        </TooltipTrigger>
                                        <TooltipContent
                                            side="top"
                                            sideOffset={6}
                                        >
                                            Related outreach
                                        </TooltipContent>
                                    </Tooltip>
                                ) : null}
                                {canShowNotesAction ? (
                                    <Tooltip>
                                        <TooltipTrigger asChild={true}>
                                            <Button
                                                aria-expanded={notesOpen}
                                                aria-label={
                                                    threadExists
                                                        ? 'Open internal thread'
                                                        : 'Comment in internal thread'
                                                }
                                                className={cn(
                                                    'text-muted-foreground hover:bg-muted hover:text-foreground size-8',
                                                    notesOpen &&
                                                        'bg-muted text-foreground',
                                                )}
                                                onClick={() =>
                                                    setNotesOpen(
                                                        (currentOpen) =>
                                                            !currentOpen,
                                                    )
                                                }
                                                ref={notesTriggerRef}
                                                size="icon"
                                                type="button"
                                                variant="ghost"
                                            >
                                                <MessageSquareText className="size-4" />
                                            </Button>
                                        </TooltipTrigger>
                                        <TooltipContent
                                            side="top"
                                            sideOffset={6}
                                        >
                                            Internal thread
                                        </TooltipContent>
                                    </Tooltip>
                                ) : null}
                            </div>
                        ) : null}
                        <span
                            className={cn(
                                'text-muted-foreground pointer-events-none absolute top-1/2 -translate-y-1/2 text-[11px] whitespace-nowrap opacity-0 transition-opacity group-hover/message:opacity-100',
                                isInbound
                                    ? 'left-full ml-2'
                                    : 'right-full mr-2',
                            )}
                        >
                            {formattedTime}
                            {isDelivered ? ' · Delivered' : null}
                        </span>
                    </div>
                </div>
                {isInbound ? null : messageAvatar}
            </div>
            {!isInbound && sentStatus !== 'sent' && (
                <div
                    className={cn(
                        'flex items-center gap-1 text-xs',
                        'justify-end pt-1 pr-2',
                        isPending
                            ? 'text-sky-500'
                            : isFailed
                              ? 'text-red-500'
                              : 'text-muted-foreground',
                    )}
                >
                    {isPending && <Loader2 className="h-3 w-3 animate-spin" />}
                    {isFailed && <AlertCircle className="h-3.5 w-3.5" />}
                    {isPending ? (
                        <span>Sending…</span>
                    ) : shortFailureLabel && fullFailureReason ? (
                        <Tooltip>
                            <TooltipTrigger asChild={true}>
                                <span className="cursor-help underline decoration-dotted underline-offset-2">
                                    Send failed — {shortFailureLabel}
                                </span>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                                {fullFailureReason}
                            </TooltipContent>
                        </Tooltip>
                    ) : (
                        <span>Send failed</span>
                    )}
                </div>
            )}
            {showDeliveryReceipt && isDelivered && (
                <div className="text-muted-foreground flex justify-end pt-1 pr-2 text-xs">
                    <Tooltip>
                        <TooltipTrigger asChild={true}>
                            <span className="cursor-help">Delivered</span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                            The carrier confirmed this text reached the phone.
                        </TooltipContent>
                    </Tooltip>
                </div>
            )}
            {showMessageNotes &&
            organizationId &&
            (notesOpen || threadExists) ? (
                <div ref={notesThreadRef}>
                    <ConversationMessageNotes
                        collapsed={!notesOpen}
                        conversationId={message.conversation_id}
                        isInbound={isInbound}
                        messageId={message.id}
                        onCollapse={() => setNotesOpen(false)}
                        onExpand={() => setNotesOpen(true)}
                        onNoteCreated={() => setThreadExists(true)}
                        onSummaryChange={setNotesSummary}
                        onConversationMutated={onConversationMutated}
                        organizationId={organizationId}
                        summary={notesSummary}
                        timezone={timezone}
                    />
                </div>
            ) : null}
            {message.role === 'user' && showRating && user?.admin && (
                <React.Fragment>
                    <div className="p-1">
                        <MessageRatingButtons
                            selectedLabel={selectedLabel}
                            isSaving={isSaving}
                            isOperationPending={isOperationPending}
                            onLabelClick={handleLabelClick}
                        />
                    </div>
                    <MessageRatingTextarea
                        selectedLabel={selectedLabel}
                        isSaving={isSaving}
                        isOperationPending={isOperationPending}
                        draftComment={draftComment}
                        hasUnsavedComment={hasUnsavedComment}
                        onCommentChange={handleCommentChange}
                        onSaveComment={handleSaveComment}
                    />
                </React.Fragment>
            )}
            {smsTrainingReview &&
                smsRunRatings.length > 0 &&
                organizationId && (
                    <div className="flex flex-col gap-2">
                        {smsRunRatings.map((smsRunRating, index) => (
                            <SmsRunRatingControl
                                key={smsRunRating.run_id}
                                organizationId={organizationId}
                                runId={smsRunRating.run_id}
                                initialLabel={smsRunRating.rating_label}
                                // Several runs can anchor to one message when a
                                // reprocessed inbound has more than one reply;
                                // number them so each thumb is distinguishable.
                                label={
                                    smsRunRatings.length > 1
                                        ? `Reply ${index + 1}`
                                        : undefined
                                }
                                onSaved={onSmsRunRated}
                            />
                        ))}
                    </div>
                )}
        </div>
    );
}
